import { isEmpty } from 'lodash';
import {
  allPossibleFields,
  availableOptions,
  availableProperties,
  fieldsThatRequiredMathTransformer,
  multiLanguageAvailableFields,
  priceMathTransformer,
  supportedLanguages,
  transformers,
} from '../constants/productUpload';
import {
  splitByCharacterTransformer,
  splittedDescriptionBullets,
  spreadDescriptionBullets,
} from '../constants/productsFeeds';

export function buildMapping(headers) {
  return Object.fromEntries(
    headers.map((field) => {
      const splittedField = getSplittedFieldBySupportedLanguages(field);
      const mapping = buildFieldMappingFromPossibleFields(field, splittedField);
      return [field, mapping];
    }),
  );
}

export function buildMappingEntries(mapping) {
  const entries = Object.entries(mapping)
    .map(([sourceField, field]) => {
      const destination = field.destination;
      if (!destination) {
        return {};
      }

      const fieldMapping = {
        source_field: sourceField,
        destination_field: destination,
      };

      if (field.type) {
        fieldMapping.type = field.type;
      }

      if (fieldsThatRequiredMathTransformer.includes(destination)) {
        fieldMapping.math_transformer = priceMathTransformer;
      }

      if (field.transformers) {
        field.transformers.forEach(({ type, value }) => {
          if (transformers.string.includes(type)) {
            fieldMapping.string_transformers = (fieldMapping.string_transformers || []).concat([{ type }]);
          } else if (transformers.string_with_value.includes(type)) {
            fieldMapping.string_transformers = (fieldMapping.string_transformers || []).concat([{ type, value }]);
          } else if (type === 'math' && value.match(/\bx\b/)) {
            fieldMapping.math_transformer = fieldMapping.math_transformer
              ? value.replace(/\bx\b/g, `(${fieldMapping.math_transformer})`)
              : value;
          }
        });
      }

      if (multiLanguageAvailableFields.includes(sourceField)) {
        fieldMapping.source_field = `${sourceField}_en`;
      }

      return fieldMapping;
    })
    .filter((field) => !isEmpty(field));

  fixDescriptionBullets(entries);
  return entries;
}

function fixDescriptionBullets(entries) {
  const bulletsEntries = entries.filter((entry) => entry?.destination_field?.startsWith('bullet_'));
  if (!bulletsEntries.length) return;

  bulletsEntries.forEach((bulletEntry) => {
    const entryIndex = entries.findIndex((entry) => entry.destination_field === bulletEntry.destination_field);
    entries.splice(entryIndex, 1);
  });

  entries.push(spreadDescriptionBullets);
}

function getSplittedFieldBySupportedLanguages(field) {
  if (field === 'supplier_id') {
    return 'supplier_id';
  }
  const splittedField = field.split('_');
  const lastPart = splittedField[splittedField.length - 1].toLowerCase();
  if (!supportedLanguages.includes(lastPart)) return field;
  return splittedField.slice(0, splittedField.length - 1).join('_');
}

function buildFieldMappingFromPossibleFields(field, splittedField) {
  if (!allPossibleFields.includes(splittedField)) return {};

  if (splittedField === 'description_bullets') {
    return splittedDescriptionBullets;
  }

  if (splittedField === 'images') {
    return {
      destination: field,
      transformers: [splitByCharacterTransformer],
    };
  }

  if (availableProperties.includes(splittedField)) {
    return {
      destination: 'property',
      type: splittedField,
    };
  }

  if (availableOptions.includes(splittedField)) {
    return {
      destination: 'option',
      type: splittedField,
    };
  }

  return { destination: splittedField };
}
